import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/errors";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Printer } from "lucide-react";

export default function PackingSlip({ admin = false }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [slip, setSlip] = useState(null);

  useEffect(() => {
    api
      .get(`${admin ? "/admin" : ""}/orders/${id}/packing-slip`)
      .then((res) => setSlip(res.data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  }, [id, admin]);

  if (!slip) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center" data-testid="packing-slip-loading">
        <Loader2 className="h-6 w-6 animate-spin text-terracotta" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl" data-testid="packing-slip-page">
      <div className="mb-6 flex items-center justify-between print:hidden">
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={() => navigate(-1)} data-testid="slip-back-to-order" className="border-limestone text-charcoal hover:bg-sand"><ArrowLeft className="mr-2 h-4 w-4" /> Back to order</Button>
          <p className="text-xs text-muted-foreground">{slip.show_prices ? "Internal packing slip" : "Client packing slip — no trade pricing"}</p>
        </div>
        <Button onClick={() => window.print()} data-testid="print-slip-button" className="bg-terracotta text-white hover:bg-terracotta-hover">
          <Printer className="mr-2 h-4 w-4" /> Print
        </Button>
      </div>

      <div className="rounded-md border border-border bg-white p-10">
        <div className="flex items-start justify-between border-b border-border pb-6">
          <div>
            <p className="font-display text-2xl font-light text-charcoal">Zelligio Connect</p>
            <p className="mt-1 text-xs uppercase tracking-[0.2em] text-warmgrey">Packing slip</p>
          </div>
          <div className="text-right">
            <p className="font-mono text-sm font-semibold text-charcoal" data-testid="slip-order-number">{slip.order_number}</p>
            <p className="text-xs text-muted-foreground">{new Date(slip.created_at).toLocaleDateString()}</p>
          </div>
        </div>

        <div className="mt-6 border-b border-border pb-6">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-warmgrey">Ship to</p>
          <p className="mt-2 text-sm font-medium text-charcoal" data-testid="slip-recipient">{slip.ship_to.recipient_name}</p>
          {slip.ship_to.company && <p className="text-sm text-warmgrey">{slip.ship_to.company}</p>}
          <p className="mt-1 text-sm text-warmgrey" data-testid="slip-address">
            {slip.ship_to.address?.line1}{slip.ship_to.address?.line2 ? `, ${slip.ship_to.address.line2}` : ""}
            <br />
            {slip.ship_to.address?.city}{slip.ship_to.address?.state ? `, ${slip.ship_to.address.state}` : ""} {slip.ship_to.address?.postal_code}
          </p>
          {slip.ship_to.delivery_instructions && <p className="mt-2 text-xs italic text-warmgrey">{slip.ship_to.delivery_instructions}</p>}
        </div>

        <table className="mt-6 w-full text-sm" data-testid="slip-lines">
          <thead>
            <tr className="border-b border-border text-left text-xs uppercase tracking-widest text-warmgrey">
              <th className="pb-2">Item</th>
              <th className="pb-2">SKU</th>
              <th className="pb-2 text-right">Boxes</th>
              <th className="pb-2 text-right">Sqft</th>
              {slip.show_prices && <th className="pb-2 text-right">Per box</th>}
              {slip.show_prices && <th className="pb-2 text-right">Subtotal</th>}
            </tr>
          </thead>
          <tbody>
            {slip.lines.map((l, i) => (
              <tr key={i} className="border-b border-border/60" data-testid={`slip-line-${i}`}>
                <td className="py-3 text-charcoal">{l.variant_label}</td>
                <td className="py-3 font-mono text-xs text-warmgrey">{l.sku}</td>
                <td className="py-3 text-right text-charcoal">{l.boxes}</td>
                <td className="py-3 text-right text-charcoal">{l.sqft}</td>
                {slip.show_prices && <td className="py-3 text-right text-charcoal">${l.trade_price_per_box.toFixed(2)}</td>}
                {slip.show_prices && <td className="py-3 text-right text-charcoal">${l.line_subtotal.toFixed(2)}</td>}
              </tr>
            ))}
          </tbody>
        </table>


        {slip.lines.some((l) => l.shipped_tracking) && (
          <div className="mt-6 border-t border-border pt-4">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-warmgrey">Tracking</p>
            {slip.lines.filter((l) => l.shipped_tracking).map((l, i) => (
              <p key={i} className="mt-1 font-mono text-xs text-charcoal">{l.variant_label}: {l.shipped_carrier} {l.shipped_tracking}</p>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
